// In-browser Vosk transcription service for uploaded audio files.
//
// Server-side Vosk transcription was retired in
// `backend/Corti_buttons/views.py::transcribe_with_vosk` — the backend now
// returns a structured 400 pointing callers at this flow. This module is the
// supported replacement: drop in an audio File/Blob, get back a transcript
// produced entirely in the browser against the Vosk service hosted at
// `VOSK_SERVICE_ORIGIN` (see `services/vosk/config.js`).
//
// The hot path is:
//   1. `decodeAudioFileToPcm16k` — Web Audio decodes the upload to mono Float32
//      PCM at 16 kHz, which is the sample rate Vosk's small-en-US model wants.
//   2. Feed the PCM to the recognizer in fixed-size chunks through
//      `window.__notesaiClientSystemSttGet`, which is wired up by `App.js`
//      (Vosk browser bundle + model archive from the manifest). The recognizer
//      returns incremental partial/final text; we keep the latest non-empty
//      value after each chunk.
//   3. After all chunks are fed, do a final flush (`finalizeVoskTranscript`)
//      and return the accumulated transcript + the decoded audio duration.
//
// Progress is reported via the optional `onProgress` callback (0..1) so the UI
// can render a determinate progress bar instead of an indeterminate spinner.

import { VOSK_SERVICE_ORIGIN } from "../../components/env";

const DEFAULT_VOSK_MODEL_ID = "vosk-model-en-us-0.22-lgraph";
const DEFAULT_TARGET_SAMPLE_RATE = 16000;
// Audio longer than this is almost certainly phone/consultation audio and
// works fine in chunks; we keep the cap generous and let the caller enforce
// tighter limits in the UI.
const MAX_AUDIO_SECONDS = 60 * 60; // 1 hour
const MAX_AUDIO_BYTES = 150 * 1024 * 1024; // 150 MB — matches Corti's old limit

const VOSK_FILE_EXTENSIONS = [
  "mp3",
  "wav",
  "m4a",
  "webm",
  "ogg",
  "oga",
  "flac",
  "mp4",
  "m4b",
  "caf",
  "aac",
];

export const VOSK_MAX_AUDIO_BYTES = MAX_AUDIO_BYTES;
export const VOSK_MAX_AUDIO_SECONDS = MAX_AUDIO_SECONDS;
export { VOSK_SERVICE_ORIGIN };

export function getVoskAllowedAudioExtensions() {
  return [...VOSK_FILE_EXTENSIONS];
}

export function getVoskAcceptAttribute() {
  return VOSK_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(",");
}

// Throws a descriptive Error if the file is something the browser can almost
// certainly not decode (wrong type / no extension / oversize). Returns the
// trimmed file name so callers can use it as a label without re-deriving.
export function validateVoskAudioFile(file) {
  if (!file) {
    throw new Error("No audio file selected.");
  }
  const name = String(file.name || "audio");
  const ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  if (!ext || !VOSK_FILE_EXTENSIONS.includes(ext)) {
    throw new Error(
      `Unsupported audio format ".${ext || "?"}". Supported: ${VOSK_FILE_EXTENSIONS.join(", ")}.`,
    );
  }
  if (typeof file.size === "number" && file.size > MAX_AUDIO_BYTES) {
    const mb = Math.round(file.size / 1024 / 1024);
    throw new Error(
      `Audio file is ${mb} MB which exceeds the ${MAX_AUDIO_BYTES / 1024 / 1024} MB limit.`,
    );
  }
  return name;
}

// In-browser Vosk STT bootstrap.
//
// `App.js` installs `window.__notesaiClientSystemSttGet` lazily from its
// `useEffect`, and `useExternalRecording.js` installs its own recognizer for
// the popup recorder. Both rely on the user opening the recorder (or simply
// waiting on App.js mount) before the model is loaded.
//
// Uploaded-audio transcription runs in a different modal that can be opened
// **before** either of those lifecycles has had a chance to install the
// getter — which produced the confusing
//   "In-browser Vosk STT is not initialized yet"
// error even though the Vosk service was reachable.
//
// To make this code path self-sufficient we lazy-install our own bootstrap
// here: fetch the manifest from `VOSK_SERVICE_ORIGIN`, load `vosk.js`, pull
// the model archive, build the recognizer, and expose it through the same
// `window.__notesaiClientSystemSttGet` global so every callsite (App.js
// preload, external recorder, this module) shares a single loaded model.
//
// If App.js or useExternalRecording later installs a real getter, we leave
// ours in place (the recognizer is interchangeable between consumers) — it
// simply isn't used. If our getter is the one that ends up driving the app,
// that's fine too.

const VOSK_BROWSER_SCRIPT_LOAD_TIMEOUT_MS = 60000;
const VOSK_MODEL_LOAD_TIMEOUT_MS = 180000;

let __bootstrapPromise = null;
let __bootstrapDone = false;

// Marker to identify a recognizer factory installed on
// `window.__notesaiClientSystemSttGet`. App.js or useExternalRecording may
// install a getter that returns the LEGACY shape (`(pcm) => Promise<{text}>`)
// which does NOT expose `.accept` / `.settle` — those consumers don't, and
// can't (without rewriting their closure), expose the lower-level primitives
// `transcribePcmWithVosk` needs to read the *full* transcript of a long
// upload. So we always run our own bootstrap and stamp the version so we
// recognise our own (or a test-stub factory-shaped) install on subsequent
// calls and don't re-download the model.
const FACTORY_MARKER = "__notesaiVoskFactoryVersion";

function getOrInstallGlobalStt() {
  if (typeof window === "undefined") return null;

  // 1. If a factory-shaped install is already present (ours or a stub
  //    that exposes `.accept`/`.settle`), reuse it. We only check for
  //    the *presence* of the marker property so tests can stamp any
  //    value they like; the contents just need to be truthy.
  if (
    typeof window.__notesaiClientSystemSttGet === "function" &&
    window.__notesaiClientSystemSttGet[FACTORY_MARKER]
  ) {
    return window.__notesaiClientSystemSttGet;
  }

  // 2. Otherwise, build and install our own factory. This guarantees the
  //    recognizer we hand back is *our* shape with `.accept` / `.settle`
  //    / `.getText` / `.getState`, regardless of whether App.js or
  //    useExternalRecording also installed a legacy getter on the global
  //    (in which case ours wins — both App.js's `warmup()` (await-only)
  //    and useExternalRecording's `__notesaiVoskServiceConfig` polling
  //    continue to work; we set the config ourselves below).
  let __sttPromise = null;
  const recognizerFactory = async () => {
    if (__sttPromise) return __sttPromise;
    __sttPromise = (async () => {
      if (!VOSK_SERVICE_ORIGIN) {
        throw new Error(
          "VOSK_SERVICE_ORIGIN is empty. Set it in env.js to the notesai-vosk-service URL.",
        );
      }
      if (typeof window === "undefined") {
        throw new Error("In-browser Vosk transcription requires a browser.");
      }

      const statusListeners = new Set();
      const setStatus = (msg) => {
        for (const fn of statusListeners) {
          try { fn(msg); } catch (_) { /* listener errors are non-fatal */ }
        }
      };
      const reportProgress = (ratio, label) => {
        if (typeof window.__notesaiVoskLoadProgress === "function") {
          try { window.__notesaiVoskLoadProgress(ratio, label); } catch (_) {}
        }
      };
      const subscribeStatus = (fn) => {
        if (typeof fn === "function") statusListeners.add(fn);
        return () => statusListeners.delete(fn);
      };
      // Stash the subscribers on `window` so App.js's warmup can pipe its
      // own toasts/progress into ours (single source of truth).
      window.__notesaiVoskSubscribeStatus = subscribeStatus;

      // 1. Fetch the manifest.
      setStatus("Fetching Vosk manifest…");
      reportProgress(0.05, "manifest");
      const manifestUrl = `${String(VOSK_SERVICE_ORIGIN).replace(/\/+$/, "")}/api/v1/manifest.json`;
      const manifestRes = await fetch(manifestUrl, { cache: "no-cache" });
      if (!manifestRes.ok) {
        throw new Error(
          `Vosk manifest HTTP ${manifestRes.status}. Make sure ${VOSK_SERVICE_ORIGIN} is reachable.`,
        );
      }
      const manifest = await manifestRes.json();
      const modelEntry =
        (Array.isArray(manifest?.models) &&
          manifest.models.find((m) => m?.id === DEFAULT_VOSK_MODEL_ID)) ||
        (Array.isArray(manifest?.models) && manifest.models[0]);
      if (!modelEntry?.url) {
        throw new Error(
          `Vosk manifest at ${manifestUrl} does not list any model.`,
        );
      }

      // 2. Load `vosk.js` (UMD bundle) into the page.
      setStatus("Loading Vosk browser bundle…");
      reportProgress(0.2, "script");
      await loadVoskBrowserScript(manifest.voskBrowser);

      // 3. Resolve `createModel` from either of the two globals the bundle
      //    exposes (see docs/vosk-service-plan.md §2.2).
      const createModel =
        (typeof window.createModel === "function" && window.createModel) ||
        (window.Vosk && typeof window.Vosk.createModel === "function"
          ? window.Vosk.createModel.bind(window.Vosk)
          : null);
      if (!createModel) {
        throw new Error(
          "Vosk browser bundle loaded but does not expose createModel().",
        );
      }

      // 4. Pull the model archive (a flat-root tar.gz of the Kaldi tree).
      setStatus("Downloading Vosk model archive (~40 MB, first run only)…");
      reportProgress(0.4, "model-download");
      const modelUrl = `${String(VOSK_SERVICE_ORIGIN).replace(/\/+$/, "")}${
        modelEntry.url.startsWith("/") ? modelEntry.url : "/" + modelEntry.url
      }`;
      // Mirror App.js's `window.__notesaiVoskServiceConfig` so any popup
      // (e.g. useExternalRecording) that polls for it after our factory
      // replaces App.js's still finds the resolved URLs.
      if (typeof window !== "undefined") {
        window.__notesaiVoskServiceConfig = {
          origin: VOSK_SERVICE_ORIGIN,
          manifestUrl,
          scriptUrl: `${String(VOSK_SERVICE_ORIGIN).replace(/\/+$/, "")}${
            (manifest?.voskBrowser?.url || "/api/v1/vosk.js").startsWith("/")
              ? manifest.voskBrowser.url
              : "/" + (manifest?.voskBrowser?.url || "/api/v1/vosk.js")
          }`,
          modelUrl,
          entryId: modelEntry.id,
        };
      }
      const model = await withTimeout(
        createModel(modelUrl),
        VOSK_MODEL_LOAD_TIMEOUT_MS,
        `Vosk model load timed out after ${VOSK_MODEL_LOAD_TIMEOUT_MS}ms (${modelUrl}).`,
      );

      // 5. Build the recognizer at 16 kHz (matches the audio we feed it).
      //    The recognizer emits `result` (finalised cumulative text) and
      //    `partialresult` events on its own internal cadence. We collect
      //    them and expose two primitives to callers:
      //
      //       stt(pcm)        — feed a chunk, return the latest text it has
      //       accept(pcm)     — feed without waiting for a settle
      //       settle(ms)      — wait until text stops changing for ~300 ms
      //                         (or `ms` total), so the *full* transcript
      //                         can be read out — not just whatever was last
      //                         emitted relative to the chunk loop.
      //
      //    Reading `latestText` only at chunk boundaries (the previous
      //    shape) drops the tail of long files because Vosk hadn't emitted
      //    its final event by the time the for-loop ended.
      setStatus("Initializing recognizer…");
      reportProgress(0.9, "recognizer");
      const recognizer = new model.KaldiRecognizer(DEFAULT_TARGET_SAMPLE_RATE);
      if (typeof recognizer.setWords === "function") recognizer.setWords(true);
      if (typeof recognizer.setPartialWords === "function") recognizer.setPartialWords(false);

      const recogState = {
        // Cumulative finalised text from `result` events (Vosk `result.text`
        // is cumulative, not incremental — every event replaces it with
        // the running total).
        latestFinal: "",
        // Most recent partial / in-progress hypothesis.
        latestPartial: "",
        // Last time the recognizer emitted any event (ms epoch).
        lastEventAt: 0,
        // Listeners awaiting a new event (for `settle()`).
        waiters: [],
        // Completed-signal listener (so `settle()` knows we're done).
        completed: false,
        // Per-buffer accounting: the vosk-browser worker replies with
        // EXACTLY ONE event (result or partialresult, possibly with
        // empty text) per acceptWaveform buffer it processes. So
        // `eventCount >= buffersFed` is a deterministic "the worker has
        // decoded every sample we sent" barrier — far more reliable than
        // any quiet-window timing heuristic.
        buffersFed: 0,
        eventCount: 0,
        // Samples fed since the recognizer last closed an utterance
        // (`result` event). Used by maybeForceEndpoint() to bound
        // utterance length on continuous speech.
        samplesSinceLastResult: 0,
        // Diagnostic counters so failure modes (e.g. "Vosk returned an
        // empty transcript") can be diagnosed from the browser console
        // without re-running the upload.
        diag: {
          // Number of `result` events received (non-empty text).
          resultEvents: 0,
          // Number of `result` events that had an empty text payload.
          emptyResultEvents: 0,
          // Number of `partialresult` events received (non-empty text).
          partialEvents: 0,
          // Number of times `recognizer.acceptWaveform` was called.
          acceptWaveformCalls: 0,
          // Number of times we called `recognizer.retrieveFinalResult()`.
          retrieveFinalCalls: 0,
          // Length (samples) of the longest single acceptWaveform call —
          // useful to confirm chunks weren't truncated to silence.
          largestAcceptSamples: 0,
          // Total samples fed in.
          totalAcceptSamples: 0,
          // Last `result.text` we received (raw, may be empty).
          lastResultText: "",
          // Last `partialresult.partial` we received.
          lastPartialText: "",
          // What `recognizer.retrieveFinalResult()` last returned (if we
          // called it).
          lastRetrieveFinal: "",
        },
      };
      const fireWaiters = () => {
        if (!recogState.waiters.length) return;
        const waiters = recogState.waiters.splice(0);
        for (const w of waiters) w();
      };
      // Vosk's `result.text` is cumulative WITHIN an utterance, but a
      // silence-based utterance boundary resets the recognizer's internal
      // counter — so the next `result.text` represents the NEW utterance
      // only, dropping everything before the silence. Uploaded audio
      // frequently has silences (pauses between thoughts, gaps between
      // recordings), so we must accumulate across boundaries. The helper
      // returns true when the new text is "the same utterance continuing"
      // and false when it's a "fresh utterance to be appended".
      const mergeUtteranceText = (prev, next) => {
        if (!prev) return { text: next, replaced: true };
        if (!next) return { text: prev, replaced: false };
        // Continuation: the new text is a strict superset (longer) of the
        // previous, OR the previous is a prefix of the new.
        if (next.length > prev.length && next.startsWith(prev)) {
          return { text: next, replaced: true };
        }
        // Continuation: the new text contains the previous as a prefix
        // (allowing trailing whitespace / finalised additions). Accept
        // any case where the new text begins with the previous.
        if (next.startsWith(prev)) {
          return { text: next, replaced: true };
        }
        // New utterance: append it.
        return { text: `${prev} ${next}`.trim(), replaced: false };
      };
      const onResult = (message) => {
        const t = message?.result?.text;
        recogState.lastEventAt = Date.now();
        recogState.eventCount += 1;
        // A `result` event ALWAYS closes the current utterance, so the
        // partial hypothesis belonging to it is now obsolete — clear it,
        // otherwise getText() could append a stale duplicate. Also reset
        // the "how long has the current utterance been running" counter
        // used by maybeForceEndpoint().
        recogState.samplesSinceLastResult = 0;
        if (typeof t === "string") {
          const trimmed = t.trim();
          recogState.diag.lastResultText = trimmed;
          if (trimmed) {
            recogState.diag.resultEvents += 1;
            const merged = mergeUtteranceText(
              recogState.latestFinal || "",
              trimmed,
            );
            recogState.latestFinal = merged.text;
          } else {
            recogState.diag.emptyResultEvents += 1;
          }
        }
        recogState.latestPartial = "";
        // Some vosk-browser versions emit an empty `result.text` for a
        // silence-only utterance (no spoken words since the last `result`).
        // We DO want to record the activity timestamp and consider the
        // recogniser active in that case — otherwise `settle()` would
        // race past the silence tail before the recogniser has had a
        // chance to emit the *next* non-empty event for the following
        // audio chunk.
        recogState.completed = true;
        fireWaiters();
      };
      const onPartial = (message) => {
        const t = message?.result?.partial;
        recogState.lastEventAt = Date.now();
        recogState.eventCount += 1;
        if (typeof t === "string" && t.trim()) {
          recogState.latestPartial = t.trim();
          recogState.diag.partialEvents += 1;
          recogState.diag.lastPartialText = recogState.latestPartial;
        }
        // Mark the recognizer as "actively processing" so `settle()`
        // knows the worker is engaged and we should wait for the
        // activity-quiet window before declaring done. Without this,
        // a recognizer that ONLY emits partials (no final `result`
        // event) would never satisfy `recogState.completed && …` in
        // `settle()` and would always time out the full totalMs —
        // which is exactly what was happening on long uploaded audio
        // with continuous speech (no silences).
        recogState.completed = true;
        fireWaiters();
      };
      const onComplete = () => {
        recogState.completed = true;
        recogState.lastEventAt = Date.now();
        fireWaiters();
      };
      if (typeof recognizer.on === "function") {
        recognizer.on("result", onResult);
        recognizer.on("partialresult", onPartial);
        recognizer.on("complete", onComplete);
      } else {
        recognizer.on("partialresult", onPartial);
      }

      const getText = () => {
        const fin = (recogState.latestFinal || "").trim();
        const part = (recogState.latestPartial || "").trim();
        if (!fin) return part;
        if (!part) return fin;
        // The partial hypothesis always belongs to the CURRENT, not yet
        // finalised utterance — i.e. speech that FOLLOWS everything in
        // `latestFinal` (onResult clears the partial whenever an
        // utterance closes). The previous `final || partial` shape threw
        // the partial away whenever any final text existed, silently
        // dropping the entire unfinalised tail of the audio. Append it
        // instead, unless the final text already contains it.
        if (fin.endsWith(part) || fin.includes(part)) return fin;
        return `${fin} ${part}`.trim();
      };
      // `recognizer.acceptWaveform(...)` accepts BOTH `Float32Array` (raw
      // PCM at the configured sample rate) AND a Web Audio `AudioBuffer`,
      // depending on the version of `vosk-browser` shipped by the service.
      // The bundle we're loading does `buffer.getChannelData(0)` inside
      // `acceptWaveform` and crashes on a bare `Float32Array`, so we wrap
      // to be safe regardless of the bundle's API surface. The helper
      // mirrors the one in `App.js` that has been working in the existing
      // App.js / useExternalRecording paths.
      const wrapPcmAsAudioBuffer = (pcm, sampleRate) => {
        const data = pcm || new Float32Array();
        if (typeof AudioBuffer === "function") {
          const audioBuffer = new AudioBuffer({
            length: data.length,
            numberOfChannels: 1,
            sampleRate,
          });
          audioBuffer.copyToChannel(data, 0);
          return audioBuffer;
        }
        // Fallback path for browsers without `AudioBuffer` (jsdom / older
        // Safari) — use a temporary `AudioContext` to allocate one.
        const AC = (typeof window !== "undefined" &&
          (window.AudioContext || window.webkitAudioContext)) || null;
        if (AC) {
          const ac = new AC({ sampleRate });
          try {
            const audioBuffer = ac.createBuffer(1, data.length, sampleRate);
            audioBuffer.copyToChannel(data, 0);
            return audioBuffer;
          } finally {
            try { ac.close(); } catch (_) { /* close failure is non-fatal */ }
          }
        }
        // No Web Audio at all (jsdom, Node test runner, very old
        // browsers). Return the raw Float32Array and let the recognizer
        // decide; the bundled `vosk-browser` does support Float32Array
        // directly, so this is a safe pass-through for the test environment.
        return data;
      };

      const accept = (pcmFloat32, inputSampleRate = DEFAULT_TARGET_SAMPLE_RATE) => {
        if (typeof recognizer.acceptWaveform !== "function") {
          throw new Error("Vosk recognizer missing acceptWaveform API");
        }
        const normalized =
          inputSampleRate === DEFAULT_TARGET_SAMPLE_RATE
            ? pcmFloat32
            : downsampleTo16kMono(pcmFloat32, inputSampleRate);
        if (!normalized || !normalized.length) return;
        // Wrap in an AudioBuffer so the bundle's `buffer.getChannelData(0)`
        // succeeds on all versions. Bundle wrapped via the legacy
        // `app.useExternalRecording` pipeline already does this; the
        // AudioUploader path was passing a Float32Array directly and
        // crashing.
        const wrapped = wrapPcmAsAudioBuffer(normalized, DEFAULT_TARGET_SAMPLE_RATE);
        recogState.diag.acceptWaveformCalls += 1;
        recogState.diag.totalAcceptSamples += normalized.length;
        if (normalized.length > recogState.diag.largestAcceptSamples) {
          recogState.diag.largestAcceptSamples = normalized.length;
        }
        recogState.buffersFed += 1;
        recogState.samplesSinceLastResult += normalized.length;
        recognizer.acceptWaveform(wrapped);
      };
      // Feed a silence PCM (all-zero samples at the configured sample
      // rate) of `silenceMs` length so Vosk's KaldiRecognizer detects
      // an utterance boundary and finalizes the current utterance with
      // a `result` event. Vosk's default silence-detection threshold
      // for utterance boundaries is ~700 ms; a single 200 ms tail
      // (the previous attempt) was treated as part of the same
      // utterance and produced no `result` event. We default to
      // 1 second so the silence comfortably exceeds the threshold
      // across recognizer builds.
      const buildSilenceBuffer = (silenceMs) => {
        const numSamples = Math.max(
          1,
          Math.round((silenceMs / 1000) * DEFAULT_TARGET_SAMPLE_RATE),
        );
        if (typeof AudioBuffer === "function") {
          const audioBuffer = new AudioBuffer({
            length: numSamples,
            numberOfChannels: 1,
            sampleRate: DEFAULT_TARGET_SAMPLE_RATE,
          });
          return audioBuffer;
        }
        const AC =
          (typeof window !== "undefined" &&
            (window.AudioContext || window.webkitAudioContext)) || null;
        if (AC) {
          const ac = new AC({ sampleRate: DEFAULT_TARGET_SAMPLE_RATE });
          try {
            return ac.createBuffer(1, numSamples, DEFAULT_TARGET_SAMPLE_RATE);
          } finally {
            try { ac.close(); } catch (_) { /* close failure is non-fatal */ }
          }
        }
        return new Float32Array(numSamples);
      };
      // NOTE: silence buffers must be built FRESH for every accept call.
      // vosk-browser's client `acceptWaveform` TRANSFERS the underlying
      // ArrayBuffer to the worker (postMessage transfer list), detaching
      // it — a cached/reused AudioBuffer would be sent EMPTY the second
      // time and silently do nothing.
      const feedSilence = (silenceMs) => {
        if (typeof recognizer.acceptWaveform !== "function") return false;
        const buf = buildSilenceBuffer(silenceMs);
        const numSamples =
          typeof buf?.length === "number"
            ? buf.length
            : Math.round((silenceMs / 1000) * DEFAULT_TARGET_SAMPLE_RATE);
        recogState.diag.acceptWaveformCalls += 1;
        recogState.diag.totalAcceptSamples += numSamples;
        recogState.buffersFed += 1;
        try {
          recognizer.acceptWaveform(buf);
          return true;
        } catch (_) {
          return false;
        }
      };
      const forceFinalViaSilenceTail = ({ silenceMs = 1000 } = {}) => {
        feedSilence(silenceMs);
      };
      // Bound utterance length on continuous speech: if the recognizer
      // hasn't closed an utterance (no `result` event) for more than
      // `maxUtteranceSeconds` of FED audio, inject a silence gap to force
      // an endpoint. This banks the text into `latestFinal` regularly
      // instead of letting an ever-growing stretch of transcript live
      // only in the fragile partial hypothesis (and it also bounds the
      // Kaldi decoder's per-utterance lattice/memory growth, which can
      // destabilise the WASM worker on very long uninterrupted speech).
      const maybeForceEndpoint = ({
        maxUtteranceSeconds = 30,
        silenceMs = 1000,
      } = {}) => {
        const maxSamples = Math.max(
          1,
          Math.round(maxUtteranceSeconds * DEFAULT_TARGET_SAMPLE_RATE),
        );
        if (recogState.samplesSinceLastResult < maxSamples) return false;
        recogState.samplesSinceLastResult = 0;
        return feedSilence(silenceMs);
      };
      // Wait until the recognizer has been quiet for ~`quietMs` (default
      // 300 ms) or `totalMs` total (default 3000 ms). Resolves with the
      // final/last-known text.
      //
      // For *uploaded* audio with no long silences, Vosk may emit only
      // `partialresult` events and never a final `result`. Falling back
      // to `latestPartial` would give the user only the last few seconds
      // of in-progress text. To get the full transcript we issue a final
      // forced retrieval — `retrieveFinalResult()` — and prefer its
      // cumulative text. This is what the underlying C++ engine expects
      // when the audio stream is "closed" (vs the live-mic case where
      // silence boundaries naturally trigger `result` events).
      const flushFinal = () => {
        if (typeof recognizer.retrieveFinalResult === "function") {
          try {
            recogState.diag.retrieveFinalCalls += 1;
            const final = recognizer.retrieveFinalResult();
            const txt = final?.result?.text;
            recogState.diag.lastRetrieveFinal =
              typeof txt === "string" ? txt.trim() : "";
            if (typeof txt === "string") {
              const trimmed = txt.trim();
              if (trimmed) {
                const merged = mergeUtteranceText(
                  recogState.latestFinal || "",
                  trimmed,
                );
                recogState.latestFinal = merged.text;
              }
            }
          } catch (_) {
            // Some vosk-browser builds throw if called too early; ignore.
          }
        }
      };
      // Canonical "I have a complete audio file, give me the transcript"
      // entry point. We:
      //   1. Wait for the worker's decode queue to drain so the cumulative
      //      text inside the recogniser is complete.
      //   2. Feed a final silence tail to guarantee the trailing audio
      //      chunk is bound by an utterance boundary (some models — e.g.
      //      vosk-model-en-us-0.22-lgraph — need a longer silence than
      //      the small model to finalise).
      //   3. Wait again so the silence-induced `result` event fires.
      //   4. Call `retrieveFinalResult()` ONCE (it's destructive — calling
      //      it again clears the buffer). Its text is merged into the
      //      accumulated `latestFinal`.
      //
      // This is the most reliable path for uploaded audio. The chunked
      // accept loop is what fills `latestFinal` opportunistically via
      // `result` events; this function is what guarantees we also get
      // the trailing chunk's text even when the recogniser's
      // silence-detection threshold exceeds our per-chunk silence tail.
      const closeAndGetFinal = async ({
        finalSilenceMs = FEED_FINAL_SILENCE_MS,
        drainQuietMs = 800,
        drainTotalMs = 12000,
        // ms-epoch timestamp taken BEFORE the job's audio was fed. When
        // provided, drains refuse to complete until the worker has
        // emitted at least one event after this moment (cold-worker /
        // first-upload guard — see waitForWorkerDrain).
        requireEventAfter = 0,
      } = {}) => {
        if (typeof recognizer.acceptWaveform !== "function") {
          return getText();
        }
        // Drain whatever the worker has buffered so far.
        await waitForWorkerDrain({
          quietMs: drainQuietMs,
          totalMs: drainTotalMs,
          requireEventAfter,
        });
        // Feed a longer silence tail to force-finalise any trailing
        // utterance. We use AudioBuffer (zero-initialised by spec).
        let tailFedAt = 0;
        try {
          const tailLen = Math.max(
            1,
            Math.round((finalSilenceMs / 1000) * DEFAULT_TARGET_SAMPLE_RATE),
          );
          let tailBuf;
          if (typeof AudioBuffer === "function") {
            tailBuf = new AudioBuffer({
              length: tailLen,
              numberOfChannels: 1,
              sampleRate: DEFAULT_TARGET_SAMPLE_RATE,
            });
          } else {
            tailBuf = new Float32Array(tailLen);
          }
          recogState.diag.acceptWaveformCalls += 1;
          recogState.diag.totalAcceptSamples += tailLen;
          if (tailLen > recogState.diag.largestAcceptSamples) {
            recogState.diag.largestAcceptSamples = tailLen;
          }
          recogState.buffersFed += 1;
          tailFedAt = Date.now();
          recognizer.acceptWaveform(tailBuf);
        } catch (_) { /* non-fatal */ }
        // Drain again so the silence-induced `result` event fires. Wait
        // specifically for an event emitted AFTER the tail was fed — that
        // event is the one that finalises (and carries the text of) the
        // last utterance. Waiting for "any event after the job started"
        // here let the drain exit while the tail was still queued behind
        // unprocessed audio, truncating the transcript.
        await waitForWorkerDrain({
          quietMs: drainQuietMs,
          totalMs: drainTotalMs,
          requireEventAfter: tailFedAt || requireEventAfter,
        });
        // Authoritative close-of-stream retrieval — call ONCE (destructive).
        flushFinal();
        // One last drain in case the close call itself triggered a
        // `result` event after our last drain (some vosk-browser builds
        // fire a final event in `retrieveFinalResult`).
        await waitForWorkerDrain({
          quietMs: 300,
          totalMs: 2000,
        });
        return getText();
      };
      const settle = (opts = {}) => {
        const quietMs = opts.quietMs ?? 300;
        const totalMs = opts.totalMs ?? 3000;
        const started = Date.now();
        const deadline = started + totalMs;
        return new Promise((resolve) => {
          const finish = () => resolve(getText());
          const check = () => {
            // The recognizer is "settled" once it has been quiet for
            // `quietMs` — i.e. `lastEventAt` is fresh enough to consider
            // the latest decoded state authoritative. The original
            // `recogState.completed && …` shape required a `result`
            // event (which only fires at silence boundaries) so
            // continuous audio with no long silences would never
            // satisfy the gate and always timed out at totalMs.
            //
            // Note: we deliberately do NOT call `flushFinal()` here
            // — that helper is destructive (resets the recognizer's
            // current utterance) and is only safe to call ONCE. The
            // chunking-with-silence-tail loop in `transcribePcmWithVosk`
            // is what guarantees the full cumulative text is captured;
            // by the time `settle()` runs, all expected `result`
            // events have already been delivered.
            if (Date.now() - recogState.lastEventAt >= quietMs) {
              return finish();
            }
            if (Date.now() >= deadline) {
              return finish();
            }
            recogState.waiters.push(check);
            // The recognizer's events will call `check()` via `fireWaiters`.
            // As a safety net, schedule a poll too so we don't hang if the
            // recognizer stops emitting events on its own.
            setTimeout(check, Math.min(quietMs, 200));
          };
          check();
        });
      };
      // Wait until the recognizer's partial text stops changing for
      // `quietMs` (default 600 ms) or `totalMs` total (default 8000 ms).
      // Vosk's Web Worker decodes `acceptWaveform`-fed audio on a worker
      // thread; the engine only updates `partial.text` AFTER it has
      // processed a chunk. Calling `retrieveFinalResult()` before the
      // worker has caught up returns an incomplete cumulative transcript
      // (and resets the recognizer's internal buffer, losing the rest).
      // Polling on partial-text stability lets the worker drain before
      // we flush.
      const waitForWorkerDrain = async (opts = {}) => {
        const quietMs = opts.quietMs ?? 600;
        const totalMs = opts.totalMs ?? 8000;
        // COLD-WORKER GUARD (fixes "empty transcript on first attempt,
        // works on second"): on the very first upload after page load the
        // WASM worker takes several seconds before it emits its FIRST
        // event. During that window the text signature is trivially
        // "stable" (nothing has changed because nothing has happened),
        // so the old check returned after `quietMs` — BEFORE any decoding
        // had occurred — and the destructive `retrieveFinalResult()`
        // downstream then produced an empty transcript. When
        // `requireEventAfter` (a ms-epoch timestamp taken before the
        // audio was fed) is provided, we refuse to declare the worker
        // drained until it has emitted at least one event AFTER that
        // moment — bounded by `firstEventGraceMs` so silence-only audio
        // on builds that never emit events can't hang the drain forever.
        const requireEventAfter = Number.isFinite(opts.requireEventAfter)
          ? opts.requireEventAfter
          : 0;
        const firstEventGraceMs = opts.firstEventGraceMs ?? 30000;
        const started = Date.now();
        const deadline = started + totalMs;
        // Seed with the CURRENT signature so pre-existing text doesn't
        // register as a "change" on the first poll.
        let lastSignature = `${recogState.latestPartial}|${recogState.latestFinal}`;
        let lastChangeAt = Date.now();
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 100));
          // Combine partial + final into one signature so we don't
          // mistake a finishing decode (partial → empty, final updated)
          // for stability.
          const signature = `${recogState.latestPartial}|${recogState.latestFinal}`;
          if (signature !== lastSignature) {
            lastSignature = signature;
            lastChangeAt = Date.now();
            continue;
          }
          // Cold-worker guard: no event has arrived for this job yet, so
          // "quiet" means "hasn't started", not "done". Keep waiting (up
          // to the grace window).
          if (
            requireEventAfter &&
            recogState.lastEventAt < requireEventAfter &&
            Date.now() - started < firstEventGraceMs
          ) {
            continue;
          }
          // Require the EVENT stream to be quiet too, not just the text:
          // silence-only stretches produce empty `result` events that
          // don't change the signature but do mean the worker is still
          // actively chewing through the fed audio.
          if (
            recogState.lastEventAt &&
            Date.now() - recogState.lastEventAt < quietMs
          ) {
            continue;
          }
          // Quiet window reached — worker is caught up. Hold for a small
          // grace period before declaring done.
          if (Date.now() - lastChangeAt >= quietMs) return;
        }
      };

      // Resolve once the recognizer has emitted at least one event at or
      // after `ts` (ms epoch), or after `timeoutMs`. The vosk-browser
      // worker posts exactly one event (result OR partialresult — even
      // with empty text) per processed acceptWaveform buffer, so this is
      // a reliable "the worker finished chunk N" acknowledgement used to
      // flow-control the feed loop in `transcribePcmWithVosk`.
      const waitForEventAfter = (ts, timeoutMs = 60000) =>
        new Promise((resolve) => {
          const deadline = Date.now() + timeoutMs;
          const check = () => {
            if (recogState.lastEventAt >= ts) return resolve(true);
            if (Date.now() >= deadline) return resolve(false);
            recogState.waiters.push(check);
            setTimeout(check, 100);
          };
          check();
        });

      // Resolve once the worker has acknowledged (with one event per
      // buffer) all but `allowLagBuffers` of the buffers fed so far —
      // i.e. `eventCount >= buffersFed - allowLagBuffers`. With
      // allowLagBuffers=0 this is THE deterministic "every sample we
      // sent has been decoded" barrier. Resolves false on timeout.
      const waitForProcessedBuffers = async (opts = {}) => {
        const allowLagBuffers = Math.max(0, opts.allowLagBuffers ?? 0);
        const timeoutMs = opts.timeoutMs ?? 120000;
        const deadline = Date.now() + timeoutMs;
        for (;;) {
          if (
            recogState.eventCount >=
            recogState.buffersFed - allowLagBuffers
          ) {
            return true;
          }
          if (Date.now() >= deadline) return false;
          await new Promise((r) => setTimeout(r, 50));
        }
      };

      reportProgress(1, "ready");
      setStatus("Vosk ready");
      window.__notesaiClientSystemSttReady = true;

      // Backwards-compatible single-call stream API. New callers should
      // prefer the richer `accept`/`settle` API exported on the function's
      // `.accept` / `.settle` properties.
      const stt = async (pcmFloat32, options = {}) => {
        accept(
          pcmFloat32,
          Number(options?.inputSampleRate) || DEFAULT_TARGET_SAMPLE_RATE,
        );
        // Wait a bit for any pending `result` event to fire before reading
        // back, but don't wait the full settle — that's the chunk loop's
        // job.
        await new Promise((resolve) => setTimeout(resolve, 100));
        return { text: getText() };
      };
      stt.accept = accept;
      stt.settle = (opts) =>
        settle({ quietMs: opts?.quietMs, totalMs: opts?.totalMs });
      stt.flushFinal = flushFinal;
      stt.forceFinal = forceFinalViaSilenceTail;
      stt.maybeForceEndpoint = maybeForceEndpoint;
      stt.waitForDrain = waitForWorkerDrain;
      stt.closeAndGetFinal = closeAndGetFinal;
      stt.waitForEventAfter = waitForEventAfter;
      stt.waitForProcessedBuffers = waitForProcessedBuffers;
      stt.getText = getText;
      stt.getState = () => ({ ...recogState });
      // Clear all accumulated text/state before a NEW upload job. The
      // recognizer (and its loaded model) is a page-lifetime singleton,
      // so without this a second upload's transcript would still contain
      // the first upload's text (recogState.latestFinal accumulates
      // across `result` events forever).
      stt.resetForNewJob = () => {
        recogState.latestFinal = "";
        recogState.latestPartial = "";
        recogState.lastEventAt = 0;
        recogState.completed = false;
        recogState.buffersFed = 0;
        recogState.eventCount = 0;
        recogState.samplesSinceLastResult = 0;
        recogState.diag.resultEvents = 0;
        recogState.diag.emptyResultEvents = 0;
        recogState.diag.partialEvents = 0;
        recogState.diag.acceptWaveformCalls = 0;
        recogState.diag.retrieveFinalCalls = 0;
        recogState.diag.largestAcceptSamples = 0;
        recogState.diag.totalAcceptSamples = 0;
        recogState.diag.lastResultText = "";
        recogState.diag.lastPartialText = "";
        recogState.diag.lastRetrieveFinal = "";
      };
      return stt;
    })();
    return __sttPromise;
  };

  if (typeof window !== "undefined") {
    recognizerFactory[FACTORY_MARKER] = "notesai-vosk-factory/1";
    window.__notesaiClientSystemSttGet = recognizerFactory;
    // Surface progress to anyone listening (e.g. App.js's preload toast).
    window.__notesaiClientSystemSttServiceOrigin = VOSK_SERVICE_ORIGIN;
  }
  return recognizerFactory;
}

// Load `vosk.js` (the `vosk-browser` UMD bundle) into the page. Idempotent —
// subsequent calls with the same URL are a no-op.
function loadVoskBrowserScript(manifestEntry) {
  return new Promise(async (resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("In-browser Vosk transcription requires a browser."));
      return;
    }
    if (
      typeof window.createModel === "function" ||
      (window.Vosk && typeof window.Vosk.createModel === "function")
    ) {
      resolve();
      return;
    }
    const base = String(VOSK_SERVICE_ORIGIN).replace(/\/+$/, "");
    const scriptPath =
      (manifestEntry && manifestEntry.url) || "/api/v1/vosk.js";
    const url = scriptPath.startsWith("http")
      ? scriptPath
      : `${base}${scriptPath.startsWith("/") ? scriptPath : "/" + scriptPath}`;
    const absoluteSrc = new URL(url, window.location.href).href;
    for (const s of document.querySelectorAll("script[data-vosk-src]")) {
      if (s.getAttribute("data-vosk-src") === absoluteSrc) {
        // Already injected — wait for `window.createModel` to appear.
        await waitFor(() =>
          typeof window.createModel === "function" ||
          (window.Vosk && typeof window.Vosk.createModel === "function"),
        );
        resolve();
        return;
      }
    }
    let timeoutId = null;
    const script = document.createElement("script");
    script.async = true;
    script.src = absoluteSrc;
    script.dataset.voskSrc = absoluteSrc;
    script.crossOrigin = "anonymous";
    script.onload = () => {
      clearTimeout(timeoutId);
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timeoutId);
      reject(new Error(`Failed to load Vosk browser bundle from ${absoluteSrc}`));
    };
    document.head.appendChild(script);
    timeoutId = setTimeout(() => {
      reject(
        new Error(
          `Vosk browser bundle load timed out after ${VOSK_BROWSER_SCRIPT_LOAD_TIMEOUT_MS}ms (${absoluteSrc}).`,
        ),
      );
    }, VOSK_BROWSER_SCRIPT_LOAD_TIMEOUT_MS);
  });
}

function waitFor(predicate, { timeoutMs = 5000, intervalMs = 25 } = {}) {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const check = () => {
      if (predicate()) return resolve();
      if (Date.now() - started > timeoutMs) {
        return reject(new Error("Timed out waiting for Vosk recognizer globals."));
      }
      setTimeout(check, intervalMs);
    };
    check();
  });
}

function withTimeout(promise, ms, message) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(message)), ms),
    ),
  ]);
}

// Resolve the in-browser STT function. Behaviour:
//   1. If `window.__notesaiClientSystemSttGet` was installed by App.js or
//      useExternalRecording, return that (we don't double-install).
//   2. Otherwise lazy-install our own bootstrap so callers from any modal
//      (including the upload-transcribe modal that runs before App.js's
//      useEffect has fired) get a working recognizer without any
//      race-condition surface.
//   3. Cache the bootstrap promise at module scope so concurrent calls
//      share the same recognizer.
async function ensureClientStt({ onStatus } = {}) {
  if (typeof window === "undefined") {
    throw new Error("In-browser Vosk transcription requires a browser.");
  }
  // Ensure the global is installed (no-op if App.js / useExternalRecording
  // already did it) and grab the factory.
  const factory = getOrInstallGlobalStt();
  if (typeof factory !== "function") {
    throw new Error(
      `In-browser Vosk STT could not be initialised (VOSK_SERVICE_ORIGIN=${VOSK_SERVICE_ORIGIN}).`,
    );
  }

  if (!__bootstrapPromise) {
    __bootstrapPromise = (async () => {
      try {
        const fn = await factory();
        __bootstrapDone = true;
        // After bootstrap, replace the lazy factory with a fast-path that
        // resolves to the preloaded recognizer. This keeps App.js's
        // `__notesaiClientSystemSttReady` polling path cheap (no re-runs
        // of the loader).
        if (typeof window !== "undefined") {
          window.__notesaiClientSystemSttGet = () => Promise.resolve(fn);
        }
        return fn;
      } catch (err) {
        // Reset so a retry can try again (common for transient network blips).
        __bootstrapPromise = null;
        throw err;
      }
    })();
  }
  if (typeof onStatus === "function") {
    // Pipe local onStatus into the global status broadcaster so the user
    // sees "Loading model..." updates.
    if (typeof window.__notesaiVoskSubscribeStatus === "function") {
      const unsubscribe = window.__notesaiVoskSubscribeStatus(onStatus);
      // Best-effort cleanup if the caller hangs up before we resolve.
      __bootstrapPromise.finally(() => unsubscribe && unsubscribe());
    }
  }
  return __bootstrapPromise;
}

// Read-only flag for callers that want to know whether the recognizer has
// already been initialised (e.g. to render "Ready" vs "Loading" UI).
export function isClientVoskReady() {
  return __bootstrapDone === true;
}

// For tests / advanced consumers — forces the next call to re-bootstrap.
export function _resetVoskBootstrapForTesting() {
  __bootstrapPromise = null;
  __bootstrapDone = false;
  if (typeof window !== "undefined") {
    try { delete window.__notesaiClientSystemSttGet; } catch (_) {}
    try { delete window.__notesaiClientSystemSttReady; } catch (_) {}
  }
}

// Downsample a mono Float32 buffer to 16 kHz via simple averaging. If the
// source rate is already 16 kHz (or unknown), the buffer is returned as-is
// to avoid an unnecessary copy.
function downsampleTo16kMono(input, sourceSampleRate) {
  if (!input || !input.length) return new Float32Array();
  const srcRate = Number(sourceSampleRate) || DEFAULT_TARGET_SAMPLE_RATE;
  if (srcRate === DEFAULT_TARGET_SAMPLE_RATE) return input;
  const ratio = srcRate / DEFAULT_TARGET_SAMPLE_RATE;
  if (!Number.isFinite(ratio) || ratio <= 0) return input;
  const outLength = Math.max(1, Math.floor(input.length / ratio));
  const output = new Float32Array(outLength);
  let outIndex = 0;
  let inIndex = 0;
  while (outIndex < outLength) {
    const nextInIndex = Math.min(
      input.length,
      Math.floor((outIndex + 1) * ratio),
    );
    let accum = 0;
    let count = 0;
    while (inIndex < nextInIndex) {
      accum += input[inIndex];
      inIndex += 1;
      count += 1;
    }
    output[outIndex] = count > 0 ? accum / count : 0;
    outIndex += 1;
  }
  return output;
}

// Extract mono PCM Float32 from a decoded AudioBuffer, mixing all channels
// down. Returns { pcm, sampleRate }.
export function extractMonoPcm(audioBuffer) {
  if (!audioBuffer) {
    throw new Error("AudioBuffer is empty.");
  }
  const channels = audioBuffer.numberOfChannels || 1;
  const length = audioBuffer.length || 0;
  if (length === 0) {
    return { pcm: new Float32Array(0), sampleRate: audioBuffer.sampleRate || DEFAULT_TARGET_SAMPLE_RATE };
  }
  if (channels === 1) {
    return {
      pcm: audioBuffer.getChannelData(0).slice(),
      sampleRate: audioBuffer.sampleRate || DEFAULT_TARGET_SAMPLE_RATE,
    };
  }
  const mixed = new Float32Array(length);
  for (let ch = 0; ch < channels; ch += 1) {
    const data = audioBuffer.getChannelData(ch);
    for (let i = 0; i < length; i += 1) {
      mixed[i] += data[i];
    }
  }
  for (let i = 0; i < length; i += 1) {
    mixed[i] /= channels;
  }
  return {
    pcm: mixed,
    sampleRate: audioBuffer.sampleRate || DEFAULT_TARGET_SAMPLE_RATE,
  };
}

// Decode an audio File/Blob/ArrayBuffer/URL into mono PCM at 16 kHz. Returns
// { pcm, sampleRate, durationSeconds }. Works with any format the browser's
// AudioContext supports (mp3, wav, m4a, webm, ogg, flac, mp4, …).
//
// String inputs (URLs) are downloaded first via `downloadAudioFromUrl`, which
// prefers the Supabase Storage SDK (`supabase.storage.from(bucket).download`)
// when the URL matches the app's Supabase project. Plain `fetch()` from the
// browser frequently returns HTTP 400 for /object/public/* Supabase storage
// URLs (CORS preflight / apikey header quirks), whereas the SDK attaches the
// apikey automatically.
export async function decodeAudioFileToPcm16k(audioInput) {
  if (!audioInput) {
    throw new Error("No audio provided.");
  }
  let arrayBuffer;
  if (typeof audioInput === "string") {
    arrayBuffer = await downloadAudioFromUrl(audioInput);
  } else if (audioInput instanceof ArrayBuffer) {
    arrayBuffer = audioInput;
  } else if (typeof audioInput.arrayBuffer === "function") {
    arrayBuffer = await audioInput.arrayBuffer();
  } else if (audioInput instanceof Blob) {
    arrayBuffer = await audioInput.arrayBuffer();
  } else {
    throw new Error("Unsupported audio input type.");
  }

  const AudioContextCtor =
    (typeof window !== "undefined" &&
      (window.AudioContext || window.webkitAudioContext)) ||
    null;
  if (!AudioContextCtor) {
    throw new Error("Web Audio API is not available in this browser.");
  }
  const ctx = new AudioContextCtor({ sampleRate: DEFAULT_TARGET_SAMPLE_RATE });
  let audioBuffer;
  try {
    audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
  } finally {
    try {
      await ctx.close();
    } catch (_) {
      /* AudioContext.close failures are non-fatal */
    }
  }

  const { pcm, sampleRate } = extractMonoPcm(audioBuffer);
  const downsampled =
    sampleRate === DEFAULT_TARGET_SAMPLE_RATE
      ? pcm
      : downsampleTo16kMono(pcm, sampleRate);
  const durationSeconds = audioBuffer.duration || (downsampled.length / DEFAULT_TARGET_SAMPLE_RATE);
  if (durationSeconds > MAX_AUDIO_SECONDS) {
    throw new Error(
      `Audio is ${Math.round(durationSeconds)}s long; the maximum is ${MAX_AUDIO_SECONDS}s.`,
    );
  }
  return {
    pcm: downsampled,
    sampleRate: DEFAULT_TARGET_SAMPLE_RATE,
    durationSeconds,
  };
}

// Parse a Supabase Storage public/object URL and return { bucket, objectPath }
// if it looks like one, otherwise null. Recognises:
//   https://<ref>.supabase.co/storage/v1/object/public/<bucket>/<path…>
//   https://<ref>.supabase.co/storage/v1/object/sign/<bucket>/<path…>?token=…
//   https://<ref>.supabase.co/storage/v1/object/<bucket>/<path…>
export function parseSupabaseStorageUrl(url) {
  try {
    const u = new URL(url);
    // Match *.supabase.co (project subdomain) or *.supabase.in (legacy).
    if (!/\.(supabase\.co|supabase\.in)$/i.test(u.hostname)) return null;
    const parts = u.pathname.split("/").filter(Boolean);
    // Expected: ["storage", "v1", "object", "<kind>", "<bucket>", <path…>]
    if (parts.length < 6) return null;
    if (parts[0] !== "storage" || parts[1] !== "v1" || parts[2] !== "object") {
      return null;
    }
    // The 4th segment is either "public" / "sign" / "authenticated" / "private"
    // or directly the bucket name when the URL is the bare object endpoint.
    const knownKinds = new Set(["public", "sign", "authenticated", "private"]);
    let bucket;
    let objectPathParts;
    if (knownKinds.has(parts[3])) {
      bucket = parts[4];
      objectPathParts = parts.slice(5);
    } else {
      bucket = parts[3];
      objectPathParts = parts.slice(4);
    }
    if (!bucket || !objectPathParts.length) return null;
    return {
      bucket,
      objectPath: objectPathParts.join("/"),
      hostname: u.hostname,
    };
  } catch (_) {
    return null;
  }
}

// Lazily require the Supabase client helpers without taking a hard import —
// keeps this module usable from non-app contexts (tests, scripts) that
// don't initialise Supabase.
async function loadSupabaseClients() {
  try {
    const supabaseClient = await import("../../components/supabaseClient");
    return {
      getSupabase: supabaseClient.getSupabase,
      supabasePublic: supabaseClient.supabasePublic,
    };
  } catch (e) {
    return null;
  }
}

// Download an audio URL as ArrayBuffer. Prefers the Supabase Storage SDK
// (avoids the HTTP 400 that plain fetch() against /object/public/* URLs
// produces from the browser), then falls back to a plain GET — with the
// apikey query parameter set — and finally to a bare fetch.
export async function downloadAudioFromUrl(url) {
  if (!url || typeof url !== "string") {
    throw new Error("No audio URL provided.");
  }

  const supabaseInfo = parseSupabaseStorageUrl(url);
  if (supabaseInfo) {
    const clients = await loadSupabaseClients();
    if (clients) {
      try {
        const supabase = await clients.getSupabase();
        const { data, error } = await supabase.storage
          .from(supabaseInfo.bucket)
          .download(supabaseInfo.objectPath);
        if (!error && data) {
          if (data.arrayBuffer) return await data.arrayBuffer();
          // Some versions return a Blob-like — coerce.
          if (typeof data === "object" && data.size != null) {
            return await new Response(data).arrayBuffer();
          }
        }
        if (error) {
          console.warn(
            "[downloadAudioFromUrl] Supabase SDK download failed, falling back to fetch:",
            error?.message || error,
          );
        }
      } catch (e) {
        console.warn(
          "[downloadAudioFromUrl] Supabase SDK threw, falling back to fetch:",
          e?.message || e,
        );
      }
    }
  }

  // Fallback path 1: GET with apikey query parameter (Supabase Storage
  // accepts this in lieu of a header and avoids CORS preflight issues).
  try {
    const apikey = await loadSupabaseAnonKey();
    const directUrl = apikey
      ? `${url}${url.includes("?") ? "&" : "?"}apikey=${encodeURIComponent(apikey)}`
      : url;
    const res = await fetch(directUrl, { method: "GET" });
    if (res.ok) return await res.arrayBuffer();
    console.warn(
      `[downloadAudioFromUrl] fetch returned HTTP ${res.status}; trying bare fetch.`,
    );
  } catch (e) {
    console.warn("[downloadAudioFromUrl] fallback #1 failed:", e?.message || e);
  }

  // Fallback path 2: bare fetch — works for non-Supabase origins where the
  // server returns the file directly (S3, R2, etc.).
  const res = await fetch(url, { method: "GET" });
  if (!res.ok) {
    throw new Error(
      `Failed to fetch audio URL (HTTP ${res.status}). Make sure CORS is enabled for the bucket.`,
    );
  }
  return await res.arrayBuffer();
}

// Read the anon key from env.js without taking a hard import — same
// pattern as loadSupabaseClients above.
async function loadSupabaseAnonKey() {
  try {
    const envModule = await import("../../components/env.js");
    const candidates = [
      envModule.supabaseKey,
      envModule.supabaseServiceKey,
    ].filter(Boolean);
    return candidates[0] || null;
  } catch (_) {
    return null;
  }
}

const SETTLE_QUIET_MS = 350;
const SETTLE_TOTAL_MS = 4000;
// How often (ms) to fire progress while waiting for the recognizer to
// settle, so the UI bar keeps moving even during the "finalizing" phase.
const SETTLE_PROGRESS_TICK_MS = 150;
// For uploaded files, the canonical Vosk API is: feed all audio →
// wait for the worker to drain → force the final utterance closed.
// CHUNK SIZE IS CRITICAL: the service ships vosk-browser 0.0.8, and its
// vendored demo (docs/vosk-service-plan.md, "file-input fallback") feeds
// files in 4000-sample (0.25 s @ 16 kHz) buffers — the same order of
// magnitude as the 4096-sample ScriptProcessor buffers the live-mic path
// uses. Feeding giant multi-second buffers (the previous 8 s /
// 128 000-sample chunks) exceeds what this bundle's worker processes per
// message; only a fraction of each buffer was decoded and the rest of
// the audio was silently dropped — which is why long uploads came back
// as "a few lines". Stick to the demo's 4000-sample chunk size.
const FEED_CHUNK_SAMPLES = 4000; // 0.25 s @ 16 kHz — matches vendored demo
// Max buffers allowed in the worker's queue before the feeder pauses
// (~5 s of audio). Bounds memory and keeps progress reporting honest.
const FEED_MAX_IN_FLIGHT_BUFFERS = 20;
// Emit onProgress every N chunks (N × 0.25 s of audio) so long files
// don't hammer React with thousands of state updates.
const FEED_PROGRESS_EVERY_CHUNKS = 16;
const FEED_FINAL_SILENCE_MS = 1500;

// Format a phase-progress payload for callers (`onProgress`). The shape is
// stable: { phase, ratio, secondsProcessed, totalSeconds, etaSeconds }.
// `phase` ∈ 'loading-model' | 'decoding' | 'transcribing' | 'finalizing'
// | 'done'.
function buildProgress({
  phase,
  ratio,
  secondsProcessed,
  totalSeconds,
  startedAt,
}) {
  const ratioSafe = Math.max(0, Math.min(1, Number(ratio) || 0));
  let etaSeconds = null;
  if (
    typeof startedAt === "number" &&
    secondsProcessed > 0 &&
    secondsProcessed < totalSeconds
  ) {
    const elapsedMs = Date.now() - startedAt;
    const msPerSecond = elapsedMs / secondsProcessed;
    const remainingSeconds = Math.max(0, totalSeconds - secondsProcessed);
    etaSeconds = Math.round((msPerSecond * remainingSeconds) / 1000);
  }
  return {
    phase,
    ratio: ratioSafe,
    secondsProcessed,
    totalSeconds,
    etaSeconds,
  };
}

// Run the Vosk recognizer over a pre-decoded PCM Float32 buffer. Returns the
// full transcript plus the decoded duration in seconds.
//
// The recognizer emits `result` (cumulative final text) and `partialresult`
// (in-progress hypothesis) events on its own internal cadence — typically
// every ~1-2 s of audio. Reading the latest text after each `acceptWaveform`
// is racy: for a long file, the for-loop ends before the final event has
// landed, so older code returned only the first ~80% of the transcript.
//
// To get the *full* text we instead:
//   1. Feed the PCM in fixed-size chunks while emitting per-chunk progress.
//   2. After the last chunk, call `settle()` on the recognizer, which
//      waits for the recognizer's events to stop arriving for ~300 ms
//      (or up to 4 s total) before reading back the final text.
export async function transcribePcmWithVosk(
  pcm,
  { sampleRate = DEFAULT_TARGET_SAMPLE_RATE, onProgress, onStatus } = {},
) {
  const stt = await ensureClientStt({ onStatus });
  const normalized =
    sampleRate === DEFAULT_TARGET_SAMPLE_RATE
      ? pcm
      : downsampleTo16kMono(pcm, sampleRate);
  if (!normalized || !normalized.length) {
    return { text: "", durationSeconds: 0 };
  }

  // Fresh job: clear text/state accumulated by any previous upload so
  // transcripts never leak across runs (the recognizer is a page-lifetime
  // singleton). Also note the job start so the drain logic can require at
  // least one recognizer event AFTER this job's audio was fed — the
  // cold-worker guard that fixes the "empty transcript on the very first
  // attempt, works on the second" failure mode.
  if (typeof stt.resetForNewJob === "function") stt.resetForNewJob();
  const jobStartedAt = Date.now();

  const totalSeconds = normalized.length / DEFAULT_TARGET_SAMPLE_RATE;
  const startedAt = Date.now();

  // Feed the audio in chunks each followed by a 1 s silence tail.
  // Vosk's KaldiRecognizer finalises an utterance only when it
  // detects a silence ≥ its internal threshold (~700 ms in
  // vosk-browser). For continuous uploaded audio with no long
  // silences, the entire PCM would be processed as a single
  // never-finalised utterance and `retrieveFinalResult()` returns "".
  // By interleaving each audio chunk with a long-enough silence we
  // force Vosk to emit one `result` (final) event per chunk — each
  // `result.text` covers the chunk's audio — and the chunk-by-chunk
  // final text is APPENDED via `mergeUtteranceText` to recover the
  // full transcript. The trailing silence + final accept ensures
  // the last chunk's text is also flushed.
  //
  // 5 s of audio + 1 s of silence is the sweet spot: large enough
  // that the silence exceeds Vosk's detection threshold, small
  // enough not to dwarf the audio.
  const TRANSCRIBING_PORTION = 0.9;
  if (typeof onProgress === "function") {
    onProgress(
      buildProgress({
        phase: "transcribing",
        ratio: 0,
        secondsProcessed: 0,
        totalSeconds,
        startedAt,
      }),
    );
  }

  // Feed the PCM to Vosk in the bundle's canonical small buffers (4000
  // samples = 0.25 s @ 16 kHz — the size the vendored vosk-browser demo
  // and the live-mic ScriptProcessor path both use). Oversized buffers
  // are NOT fully processed by vosk-browser 0.0.8's worker — the excess
  // audio is silently dropped, which truncated long uploads to a few
  // lines. Windowed flow control (at most FEED_MAX_IN_FLIGHT_BUFFERS
  // buffers outstanding) keeps the worker queue shallow so progress is
  // truthful and finalization starts nearly caught up.
  const audioSamplesPerChunk = FEED_CHUNK_SAMPLES;
  const totalChunks = Math.max(
    1,
    Math.ceil(normalized.length / audioSamplesPerChunk),
  );
  let lastAcceptAt = jobStartedAt;
  for (let i = 0; i < totalChunks; i += 1) {
    const start = i * audioSamplesPerChunk;
    const end = Math.min(normalized.length, start + audioSamplesPerChunk);
    if (end > start) {
      const chunk =
        normalized.length === end && start === 0
          ? normalized
          : normalized.subarray(start, end);
      lastAcceptAt = Date.now();
      stt.accept(chunk, DEFAULT_TARGET_SAMPLE_RATE);
    }
    // Bound utterance length: if no `result` event has closed an
    // utterance for >30 s of fed audio (continuous speech), inject a
    // 1 s silence gap to force an endpoint so the text so far is banked
    // into the durable final transcript instead of accumulating in the
    // fragile partial hypothesis.
    if (typeof stt.maybeForceEndpoint === "function") {
      stt.maybeForceEndpoint({ maxUtteranceSeconds: 30, silenceMs: 1000 });
    }
    // FLOW CONTROL: pause feeding whenever the worker is more than
    // FEED_MAX_IN_FLIGHT_BUFFERS (~5 s of audio) behind. The worker
    // posts exactly one event per buffer, so buffersFed − eventCount
    // is the exact queue depth.
    if (typeof stt.waitForProcessedBuffers === "function") {
      await stt.waitForProcessedBuffers({
        allowLagBuffers: FEED_MAX_IN_FLIGHT_BUFFERS,
        timeoutMs: 30000,
      });
    } else {
      // Legacy/stub recognizers (tests) — keep the old cooperative yield.
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
    const isLastChunk = i === totalChunks - 1;
    if (
      typeof onProgress === "function" &&
      (isLastChunk || i % FEED_PROGRESS_EVERY_CHUNKS === 0)
    ) {
      const ratio = ((i + 1) / totalChunks) * TRANSCRIBING_PORTION;
      const secondsProcessed = Math.min(
        totalSeconds,
        ((i + 1) * audioSamplesPerChunk) / DEFAULT_TARGET_SAMPLE_RATE,
      );
      onProgress(
        buildProgress({
          phase: "transcribing",
          ratio,
          secondsProcessed,
          totalSeconds,
          startedAt,
        }),
      );
    }
  }

  // HARD BARRIER: wait until the worker has acknowledged EVERY buffer we
  // fed (one event per buffer) before starting finalization. This is the
  // deterministic guarantee that the whole file was decoded — no timing
  // heuristics involved. The timeout scales with audio length purely as
  // a safety net against a crashed worker.
  if (typeof stt.waitForProcessedBuffers === "function") {
    if (typeof onStatus === "function") {
      onStatus("Waiting for recognizer to finish decoding…");
    }
    await stt.waitForProcessedBuffers({
      allowLagBuffers: 0,
      timeoutMs: Math.max(30000, Math.round(totalSeconds * 1500) + 10000),
    });
  }

  // Phase N: close the stream. The canonical Vosk flow for file
  // transcription is: wait for worker to drain → feed a final silence
  // tail → drain again → call `retrieveFinalResult()` exactly once.
  // `retrieveFinalResult()` is destructive (clears the recogniser's
  // current utterance), so it must only be the LAST thing we do.
  if (typeof onStatus === "function") onStatus("Finalizing transcription…");
  if (typeof onProgress === "function") {
    onProgress(
      buildProgress({
        phase: "finalizing",
        ratio: TRANSCRIBING_PORTION,
        secondsProcessed: totalSeconds,
        totalSeconds,
        startedAt,
      }),
    );
  }
  // Poll the recognizer's state during finalization so the progress
  // bar keeps moving smoothly instead of stalling while we wait for
  // the worker.
  let finalizeProgressTimer = null;
  const startedFinalizeAt = Date.now();
  const stopFinalizeTimer = () => {
    if (finalizeProgressTimer) clearInterval(finalizeProgressTimer);
    finalizeProgressTimer = null;
  };
  if (typeof onProgress === "function") {
    finalizeProgressTimer = setInterval(() => {
      const elapsed = Date.now() - startedFinalizeAt;
      // give the finalize up to (closeAndGetFinal drain budgets + buffer)
      const budgetMs = 12000 + 12000 + 2000 + 2000;
      const finalizeRatio = Math.min(1, elapsed / budgetMs);
      const ratio = TRANSCRIBING_PORTION + finalizeRatio * (1 - TRANSCRIBING_PORTION);
      onProgress(
        buildProgress({
          phase: "finalizing",
          ratio,
          secondsProcessed: totalSeconds,
          totalSeconds,
          startedAt,
        }),
      );
    }, SETTLE_PROGRESS_TICK_MS);
  }
  let text = "";
  try {
    if (typeof stt.closeAndGetFinal === "function") {
      // Scale the drain budget with audio length: the worker decodes
      // at roughly real-time speed, so a fixed 12 s cap silently
      // truncated (or, on a cold first run, emptied) anything longer
      // than ~12 s. The quiet-window check exits far earlier once the
      // worker is actually caught up; this is only the hard ceiling.
      const drainTotalMs = Math.max(
        12000,
        Math.round(totalSeconds * 1500) + 5000,
      );
      text = await stt.closeAndGetFinal({
        finalSilenceMs: FEED_FINAL_SILENCE_MS,
        drainQuietMs: 800,
        drainTotalMs,
        // At least one event must arrive AFTER the last audio chunk was
        // fed before the first drain can complete (the silence tail then
        // gets its own, stricter, requireEventAfter inside).
        requireEventAfter: lastAcceptAt,
      });
    } else {
      // Defensive fallback if a stale STT installer is present.
      text = await stt.settle({
        quietMs: SETTLE_QUIET_MS,
        totalMs: SETTLE_TOTAL_MS,
      });
    }
  } finally {
    stopFinalizeTimer();
  }
  // Diagnostic dump — ALWAYS logged so truncation can be diagnosed from
  // the console without re-running the upload: compare totalSeconds
  // (decoded audio) against your file's real length (if these differ,
  // the browser's audio DECODE truncated the file before Vosk ever saw
  // it), and buffersFed vs eventCount (if these differ, the recognizer
  // worker stopped acknowledging buffers mid-file).
  if (typeof console !== "undefined" && typeof console.warn === "function") {
    try {
      const state = stt.getState ? stt.getState() : null;
      const diag = state?.diag || {};
      const finalText = (text || "").trim();
      const summary = {
        totalSamples: normalized.length,
        totalSeconds,
        buffersFed: state?.buffersFed,
        eventCount: state?.eventCount,
        acceptWaveformCalls: diag.acceptWaveformCalls,
        totalAcceptSamples: diag.totalAcceptSamples,
        largestAcceptSamples: diag.largestAcceptSamples,
        resultEvents: diag.resultEvents,
        emptyResultEvents: diag.emptyResultEvents,
        partialEvents: diag.partialEvents,
        retrieveFinalCalls: diag.retrieveFinalCalls,
        transcriptChars: finalText.length,
        transcriptWords: finalText ? finalText.split(/\s+/).length : 0,
        lastResultText: diag.lastResultText,
        lastPartialText: diag.lastPartialText,
        lastRetrieveFinal: diag.lastRetrieveFinal,
      };
      if (!finalText) {
        console.warn(
          "[vosk] transcribePcmWithVosk returned an empty transcript. Diagnostic dump:",
          summary,
        );
      } else {
        console.info("[vosk] transcription finished. Diagnostic dump:", summary);
      }
    } catch (_) { /* diagnostics never crash the call */ }
  }
  if (typeof onProgress === "function") {
    onProgress(
      buildProgress({
        phase: "done",
        ratio: 1,
        secondsProcessed: totalSeconds,
        totalSeconds,
        startedAt,
      }),
    );
  }
  return {
    text: (text || "").trim(),
    durationSeconds: totalSeconds,
    // Diagnostic counters (always present so callers can fold them into
    // their error messages). Empty on success would be unusual but is
    // preserved as-is so a missing counter isn't mistaken for 0.
    diag:
      typeof stt.getState === "function" && stt.getState()?.diag
        ? { ...stt.getState().diag }
        : undefined,
  };
}

// Public entry point: upload a File/Blob → transcript.
//
//   const { text, durationSeconds } = await transcribeUploadedAudio(file, {
//     onProgress: ({ phase, ratio, secondsProcessed, totalSeconds, etaSeconds }) => {
//       setProgress(ratio);
//       setEta(etaSeconds);
//     },
//     onStatus: (msg) => toast({ title: msg }),
//   });
//
export async function transcribeUploadedAudio(audioInput, options = {}) {
  const fileName =
    (audioInput && typeof audioInput === "object" && audioInput.name) ||
    "audio";
  if (audioInput && typeof audioInput === "object" && audioInput.size) {
    validateVoskAudioFile({
      name: fileName,
      size: audioInput.size,
    });
  }
  if (typeof options.onStatus === "function") options.onStatus("Decoding audio…");
  if (typeof options.onProgress === "function") {
    // Emit a 0% decode progress so the bar transitions smoothly from
    // "selected file" into the decode phase.
    options.onProgress({
      phase: "decoding",
      ratio: 0,
      secondsProcessed: 0,
      totalSeconds: 0,
      etaSeconds: null,
    });
  }
  const { pcm, durationSeconds } = await decodeAudioFileToPcm16k(audioInput);
  if (typeof options.onStatus === "function") options.onStatus("Transcribing with Vosk…");
  const { text } = await transcribePcmWithVosk(pcm, {
    sampleRate: DEFAULT_TARGET_SAMPLE_RATE,
    onProgress: options.onProgress,
    onStatus: options.onStatus,
  });
  return {
    text: (text || "").trim(),
    durationSeconds,
    modelId: DEFAULT_VOSK_MODEL_ID,
    serviceOrigin: VOSK_SERVICE_ORIGIN,
  };
}

const transcribeUploadedAudioService = {
  transcribeUploadedAudio,
  transcribePcmWithVosk,
  decodeAudioFileToPcm16k,
  downloadAudioFromUrl,
  parseSupabaseStorageUrl,
  extractMonoPcm,
  validateVoskAudioFile,
  getVoskAllowedAudioExtensions,
  getVoskAcceptAttribute,
  isClientVoskReady,
  DEFAULT_VOSK_MODEL_ID,
  VOSK_MAX_AUDIO_BYTES,
  VOSK_MAX_AUDIO_SECONDS,
  VOSK_SERVICE_ORIGIN,
};

export { DEFAULT_VOSK_MODEL_ID };
export default transcribeUploadedAudioService;